// src/pages/JobSeekerProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useMessaging } from '../contexts/MessagingContext';
import JobSeekerReviews from '../components/jobseeker/JobSeekerReviews';
import StarRating from '../components/common/StarRating';
import JobSeekerEditForm from '../components/jobseeker/JobSeekerEditForm';
import '../styles/forms.css';
import { updateUserProfile } from '../services/firestore';

const JobSeekerProfilePage = () => {
  const { id } = useParams();
  const { currentUser, isProvider, isParent } = useAuth();
  const { startConversation } = useMessaging();
  const navigate = useNavigate();
  
  const [jobSeeker, setJobSeeker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [isEditing, setIsEditing] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState(null);
  
  // Get the user ID from the URL or use the current user's ID
  const userId = id || currentUser?.uid;
  
  // Fetch job seeker data
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setError('Invalid user ID');
      return;
    }
    
    const fetchJobSeeker = async () => {
      try {
        const jobSeekerDoc = await getDoc(doc(db, 'jobseekers', userId));
        
        if (jobSeekerDoc.exists()) {
          setJobSeeker({ id: jobSeekerDoc.id, ...jobSeekerDoc.data() });
        } else {
          setError('Job seeker not found');
        }
      } catch (err) {
        console.error('Error fetching job seeker:', err);
        setError('Failed to load job seeker information');
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobSeeker();
  }, [userId]);
  
  // Handle send message
  const handleSendMessage = async () => {
    if (!currentUser || !jobSeeker) return;
    setContactLoading(true);
    setContactError(null);
    try {
      const conversation = await startConversation(jobSeeker.uid);
      if (conversation && conversation.id) {
        // Navigate to the specific conversation
        navigate(`/messages/${conversation.id}`);
      } else {
        // Fallback to messages page if conversation creation failed
        navigate('/messages');
      }
    } catch (err) {
      setContactError('Failed to start conversation: ' + (err.message || err));
    } finally {
      setContactLoading(false);
    }
  };

  // Handle profile update
  const handleProfileUpdate = async (updatedProfile) => {
    try {
      await updateUserProfile('jobseekers', jobSeeker.id, updatedProfile);
      setJobSeeker(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          ...updatedProfile
        }
      }));
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    }
  };
  
  const { id: jobSeekerId, ...jobSeekerProfile } = jobSeeker || {};
  const profileId = id || jobSeekerId || jobSeeker?.uid;
  
  const handleShare = () => {
    const url = `${window.location.origin}/jobseeker/${profileId}`;
    if (navigator.share) {
      navigator.share({
        title: jobSeeker?.displayName || 'Caregiver Profile',
        url,
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('Profile link copied to clipboard!');
    }
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading job seeker profile...</p>
      </div>
    );
  }
  
  if (error || !jobSeeker) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error || 'Failed to load job seeker profile'}</p>
      </div>
    );
  }
  
  let certifications = Array.isArray(jobSeekerProfile.certifications) ? [...jobSeekerProfile.certifications] : [];
  if (jobSeekerProfile.criminalRecordCheck && !certifications.includes('Criminal Record Check Completed')) {
    certifications.push('Criminal Record Check Completed');
  }
  
  const isOwner = currentUser && currentUser.uid === jobSeeker.uid;
  const isPublicView = !currentUser;
  
  return (
    <div className="jobseeker-profile-page">
      {isPublicView && (
        <div className="public-profile-banner" style={{background:'#f7f7fa',color:'#444',padding:'1rem',borderRadius:'10px',marginBottom:'1.5rem',textAlign:'center',fontWeight:600}}>
          This is a public profile view. Log in to contact or interact.
        </div>
      )}
      <div className="profile-header">
        <div className="header-content">
          <h1>{jobSeeker.displayName}</h1>
          
          <div className="jobseeker-meta">
            <div className="jobseeker-rating">
              <StarRating value={jobSeeker.averageRating || 0} readonly size="medium" />
              <span className="rating-count">
                ({jobSeeker.reviewCount || 0} reviews)
              </span>
            </div>
            
            <div className="jobseeker-type">
              {jobSeekerProfile.type === 'nanny' ? 'Nanny' : 'Early Childhood Educator'}
            </div>
          </div>
        </div>
        
        <div className="header-actions">
          {currentUser && currentUser.uid === jobSeeker.uid ? (
            <button 
              className="edit-button"
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? 'Cancel Edit' : 'Edit Profile'}
            </button>
          ) : currentUser && (isProvider() || isParent()) && (
            <>
              <button className="contact-button" onClick={handleSendMessage} disabled={contactLoading}>
                {contactLoading ? 'Contacting...' : 'Contact Caregiver'}
              </button>
              {contactError && <div className="error-message">{contactError}</div>}
            </>
          )}
          <button className="share-profile-btn" onClick={handleShare} style={{marginLeft:'0.7rem',background:'#7367f0',color:'#fff',border:'none',borderRadius:'6px',padding:'0.5rem 1.1rem',fontWeight:600,cursor:'pointer'}}>Share Profile</button>
        </div>
      </div>

      {isEditing ? (
        <JobSeekerEditForm
          profile={jobSeekerProfile}
          onSave={handleProfileUpdate}
          onCancel={() => setIsEditing(false)}
        />
      ) : (
        <>
          <div className="profile-tabs">
            <button 
              className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button 
              className={`tab-button ${activeTab === 'experience' ? 'active' : ''}`}
              onClick={() => setActiveTab('experience')}
            >
              Experience & Education
            </button>
            <button 
              className={`tab-button ${activeTab === 'availability' ? 'active' : ''}`}
              onClick={() => setActiveTab('availability')}
            >
              Availability
            </button>
            <button 
              className={`tab-button ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              Reviews
            </button>
          </div>
          
          <div className="profile-content">
            {activeTab === 'details' && (
              <div className="profile-details">
                <div className="details-grid">
                  <div className="details-section">
                    <h2>About</h2>
                    <p><strong>Type:</strong> {jobSeekerProfile.type === 'nanny' ? 'Nanny' : 'Early Childhood Educator'}</p>
                    <div className="details-section">
                      <h2>Contact Information</h2>
                      {jobSeekerProfile.phone && isOwner && (
                        <p><strong>Phone:</strong> {jobSeekerProfile.phone}</p>
                      )}
                      {isOwner && <p><strong>Email:</strong> {jobSeeker.email || 'Email not provided'}</p>}
                    </div>
                  </div>
                  
                  <div className="details-section">
                    <h2>Location</h2>
                    {jobSeekerProfile.address && isOwner && (
                      <p><strong>Address:</strong> {jobSeekerProfile.address}</p>
                    )}
                  </div>
                  
                  <div className="details-section">
                    <h2>Languages</h2>
                    {jobSeekerProfile.languages && jobSeekerProfile.languages.length > 0 ? (
                      <div className="languages-list">
                        {jobSeekerProfile.languages.map(language => (
                          <span className="language-tag" key={language}>{language}</span>
                        ))}
                      </div>
                    ) : (
                      <p>No languages specified</p>
                    )}
                  </div>
                  
                  <div className="details-section">
                    <h2>Certifications</h2>
                    {certifications.length > 0 ? (
                      <div className="certifications-list">
                        {certifications.map(certification => (
                          <div className="certification-item" key={certification}>
                            <span className="certification-name">{certification}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>No certifications specified</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'experience' && (
              <div className="experience-education">
                <div className="experience-section">
                  <h2>Work Experience</h2>
                  {jobSeekerProfile.experience ? (
                    <div className="experience-content">
                      <p>{jobSeekerProfile.experience}</p>
                    </div>
                  ) : (
                    <p>No work experience provided</p>
                  )}
                </div>
                
                <div className="education-section">
                  <h2>Education</h2>
                  {jobSeekerProfile.education ? (
                    <div className="education-content">
                      <p>{jobSeekerProfile.education}</p>
                    </div>
                  ) : (
                    <p>No education information provided</p>
                  )}
                </div>
                
                <div className="recommendations-section">
                  <h2>Recommendations</h2>
                  {jobSeekerProfile.recommendations && jobSeekerProfile.recommendations.length > 0 ? (
                    <div className="recommendations-list">
                      {jobSeekerProfile.recommendations.map((recommendation, index) => (
                        <div className="recommendation-item" key={index}>
                          <div className="recommendation-content">
                            <p className="recommendation-text">"{recommendation.text}"</p>
                            <div className="recommender-info">
                              <p className="recommender-name">{recommendation.name}</p>
                              {recommendation.position && recommendation.organization && (
                                <p className="recommender-title">{recommendation.position} at {recommendation.organization}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No recommendations provided</p>
                  )}
                </div>
              </div>
            )}
            
            {activeTab === 'availability' && (
              <div className="availability-section">
                <h2>Availability</h2>
                {jobSeekerProfile.availability ? (
                  <div className="hours-grid">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                      const key = day.toLowerCase();
                      const slot = jobSeekerProfile.availability[key];
                      const formatTime12Hour = (timeStr) => {
                        if (!timeStr) return '';
                        const [hour, minute] = timeStr.split(':');
                        const date = new Date();
                        date.setHours(Number(hour), Number(minute));
                        return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
                      };
                      return (
                        <div className="day-hours" key={day}>
                          <div className="day-name">{day}</div>
                          <div className="hours">
                            {slot && slot.start && slot.end ? (
                              `${formatTime12Hour(slot.start)} - ${formatTime12Hour(slot.end)}`
                            ) : (
                              'Not available'
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p>No availability information provided</p>
                )}
              </div>
            )}
            {activeTab === 'reviews' && (
              <div className="reviews-section">
                <JobSeekerReviews jobSeekerId={jobSeeker.id} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default JobSeekerProfilePage;